/** RadioVisualizer · audio-reactive visuals (Web Audio API + canvas) · 3 modes */
import React, { useEffect, useRef, useState } from "react";

const SYMMETRY = 8;
const MODE_KEY = "nx_radio_viz_mode";
const MODES = [
  { id: "fractal", label: "✦ Fractal Bloom" },
  { id: "rings", label: "◎ Waveform Rings" },
  { id: "starfield", label: "✧ Starfield" },
];

export const RadioVisualizer = ({ audioRef, active }) => {
  const canvasRef = useRef(null);
  const graphRef = useRef(null);
  const rafRef = useRef(0);
  const rotRef = useRef(0);
  const starsRef = useRef(null);
  const wrapRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const modeRef = useRef("fractal");
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) || "fractal"; } catch { return "fractal"; }
  });
  modeRef.current = mode;

  const pickMode = (id) => {
    setMode(id);
    try { localStorage.setItem(MODE_KEY, id); } catch { /* noop */ }
  };

  const toggleFs = () => {
    if (!document.fullscreenElement) {
      wrapRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFs(document.fullscreenElement === wrapRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  useEffect(() => {
    if (!active) return;
    const audio = audioRef.current;
    const canvas = canvasRef.current;
    if (!audio || !canvas) return;

    if (!graphRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      try {
        const actx = new AC();
        const source = actx.createMediaElementSource(audio);
        const analyser = actx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.82;
        source.connect(analyser);
        analyser.connect(actx.destination);
        graphRef.current = {
          actx, analyser,
          data: new Uint8Array(analyser.frequencyBinCount),
          wave: new Uint8Array(analyser.fftSize),
        };
      } catch {
        return;
      }
    }
    graphRef.current.actx.resume().catch(() => {});

    const { analyser, data, wave } = graphRef.current;
    const g = canvas.getContext("2d");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const band = (from, to) => {
      let s = 0;
      for (let i = from; i < to; i++) s += data[i];
      return s / ((to - from) * 255);
    };

    const branch = (len, depth, spread) => {
      if (depth === 0 || len < 5) return;
      g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -len); g.stroke();
      g.translate(0, -len);
      g.save(); g.rotate(spread); branch(len * 0.68, depth - 1, spread); g.restore();
      g.save(); g.rotate(-spread); branch(len * 0.68, depth - 1, spread); g.restore();
    };

    const drawFractal = (w, h, cx, cy, bass, mid, high) => {
      rotRef.current += 0.0022 + bass * 0.012;
      const baseLen = Math.min(w, h) * (0.16 + bass * 0.14);
      const spread = 0.45 + mid * 0.85;
      for (let i = 0; i < SYMMETRY; i++) {
        g.save();
        g.translate(cx, cy);
        g.rotate(rotRef.current + (i * Math.PI * 2) / SYMMETRY);
        g.strokeStyle = i % 2 === 1
          ? `rgba(0, 255, 204, ${0.25 + high * 0.6})`
          : `rgba(255, 215, 0, ${0.3 + mid * 0.55})`;
        g.lineWidth = 1.1 + bass * 1.6;
        branch(baseLen, 4, spread);
        g.restore();
      }
    };

    const drawRings = (w, h, cx, cy, bass, mid, high) => {
      analyser.getByteTimeDomainData(wave);
      rotRef.current += 0.003 + bass * 0.008;
      const n = wave.length;
      const rings = [
        { r: Math.min(w, h) * 0.3 + bass * 24, amp: 34, color: `rgba(255, 215, 0, ${0.45 + mid * 0.5})`, lw: 1.6 + bass * 1.4, phase: 0 },
        { r: Math.min(w, h) * 0.19 + bass * 14, amp: 20, color: `rgba(0, 255, 204, ${0.35 + high * 0.55})`, lw: 1.2, phase: Math.PI / 3 },
      ];
      rings.forEach(({ r, amp, color, lw, phase }) => {
        g.beginPath();
        for (let i = 0; i <= n; i++) {
          const v = (wave[i % n] - 128) / 128;
          const a = (i / n) * Math.PI * 2 + rotRef.current + phase;
          const rad = r + v * amp;
          const x = cx + Math.cos(a) * rad;
          const y = cy + Math.sin(a) * rad;
          i === 0 ? g.moveTo(x, y) : g.lineTo(x, y);
        }
        g.closePath();
        g.strokeStyle = color;
        g.lineWidth = lw;
        g.stroke();
      });
    };

    const drawStarfield = (w, h, cx, cy, bass, mid, high) => {
      if (!starsRef.current) {
        starsRef.current = Array.from({ length: 180 }, () => ({
          x: Math.random() * 2 - 1, y: Math.random() * 2 - 1, z: Math.random(),
        }));
      }
      const speed = 0.003 + bass * 0.04;
      starsRef.current.forEach((s, i) => {
        s.z -= speed;
        if (s.z <= 0.02) { s.x = Math.random() * 2 - 1; s.y = Math.random() * 2 - 1; s.z = 1; }
        const k = 0.9 / s.z;
        const sx = cx + s.x * k * (w / 4);
        const sy = cy + s.y * k * (h / 4);
        if (sx < 0 || sx > w || sy < 0 || sy > h) return;
        const size = (1 - s.z) * (1.6 + high * 3);
        const alpha = (1 - s.z) * (0.5 + mid * 0.5);
        g.fillStyle = i % 3 === 0 ? `rgba(0, 255, 204, ${alpha})` : `rgba(255, 215, 0, ${alpha})`;
        g.beginPath();
        g.arc(sx, sy, Math.max(0.4, size), 0, Math.PI * 2);
        g.fill();
      });
    };

    const frame = () => {
      const w = canvas.clientWidth || 300;
      const h = canvas.clientHeight || 220;
      if (canvas.width !== w * 2 || canvas.height !== h * 2) {
        canvas.width = w * 2;
        canvas.height = h * 2;
      }
      analyser.getByteFrequencyData(data);
      const bass = band(0, 8);
      const mid = band(8, 40);
      const high = band(40, 100);
      const m = modeRef.current;

      g.setTransform(2, 0, 0, 2, 0, 0);
      g.fillStyle = m === "starfield" ? "rgba(10, 10, 15, 0.4)" : "rgba(10, 10, 15, 0.22)";
      g.fillRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      if (m === "rings") drawRings(w, h, cx, cy, bass, mid, high);
      else if (m === "starfield") drawStarfield(w, h, cx, cy, bass, mid, high);
      else drawFractal(w, h, cx, cy, bass, mid, high);

      if (m !== "starfield") {
        const orbR = 6 + bass * 26;
        const grad = g.createRadialGradient(cx, cy, 0, cx, cy, orbR);
        grad.addColorStop(0, "rgba(255, 215, 0, 0.9)");
        grad.addColorStop(1, "rgba(255, 215, 0, 0)");
        g.fillStyle = grad;
        g.beginPath();
        g.arc(cx, cy, orbR, 0, Math.PI * 2);
        g.fill();
      }

      if (!reducedMotion) rafRef.current = requestAnimationFrame(frame);
    };
    frame();

    return () => cancelAnimationFrame(rafRef.current);
  }, [active, audioRef]);

  useEffect(() => () => {
    if (graphRef.current) graphRef.current.actx.close().catch(() => {});
  }, []);

  if (!active) return null;
  return (
    <div
      ref={wrapRef}
      data-testid="radio-visualizer-wrap"
      style={{ marginBottom: 16, position: "relative", background: isFs ? "#0a0a0f" : "transparent" }}
    >
      <canvas
        ref={canvasRef}
        data-testid="radio-visualizer"
        aria-hidden="true"
        style={{
          display: "block",
          width: "100%",
          height: isFs ? "100vh" : 220,
          borderRadius: isFs ? 0 : 10,
          background: "#0a0a0f",
          border: isFs ? "none" : "1px solid rgba(255,215,0,0.25)",
        }}
      />
      <div
        className="flex items-center gap-2 flex-wrap"
        style={
          isFs
            ? { position: "absolute", bottom: 24, left: 0, right: 0, justifyContent: "center", zIndex: 2 }
            : { marginTop: 8 }
        }
      >
        {MODES.map((mo) => (
          <button
            key={mo.id}
            type="button"
            data-testid={`radio-viz-mode-${mo.id}`}
            onClick={() => pickMode(mo.id)}
            className="font-mono uppercase tracking-[0.18em]"
            style={{
              minHeight: 32,
              padding: "0 14px",
              fontSize: 10,
              fontWeight: 700,
              borderRadius: 999,
              cursor: "pointer",
              color: mode === mo.id ? "#0a0a0f" : "rgba(224,224,224,0.7)",
              background: mode === mo.id ? "linear-gradient(135deg, #D4AF37 0%, #00FFCC 100%)" : "transparent",
              border: mode === mo.id ? "1px solid transparent" : "1px solid rgba(255,215,0,0.3)",
              transition: "background-color 160ms ease, color 160ms ease",
            }}
          >
            {mo.label}
          </button>
        ))}
        <button
          type="button"
          data-testid="radio-viz-fullscreen-btn"
          onClick={toggleFs}
          title={isFs ? "Exit fullscreen" : "Fullscreen visualizer"}
          className="font-mono uppercase tracking-[0.18em]"
          style={{
            minHeight: 32,
            padding: "0 14px",
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 999,
            cursor: "pointer",
            color: "#FFD700",
            background: isFs ? "rgba(255,215,0,0.14)" : "transparent",
            border: "1px solid rgba(255,215,0,0.45)",
            transition: "background-color 160ms ease",
          }}
        >
          {isFs ? "✕ Exit" : "⛶ Fullscreen"}
        </button>
      </div>
    </div>
  );
};

export default RadioVisualizer;
